import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { useAuthContext } from "../auth/AuthContext";
import PpLoginModal from "../auth/PpLoginModal";
import { getPpToken, resolveIdentityEmail } from "../auth/ppToken";
import { jwtUserOid } from "../auth/jwt";
import { adtBellErrorKey } from "../components/adtBellLogic";
import { useDarkMode } from "../theme/useDarkMode";
import { resolveWorklistIdentity } from "./resolveWorklistIdentity";
import { useWorklistAccessGate } from "./useWorklistAccessGate";
import { buildWorklistMemberNavState } from "./worklistNavState";
import {
  getStoredProviderPicks,
  setStoredProviderPicks,
} from "./providerSelectionStorage";
import { picksFromProviders } from "./resolveRosterSeed";
import {
  IpaErrorState,
  IpaForbiddenState,
  NoIpaState,
  NpiRequiredState,
  PpLoginRequiredState,
  ResolvingIpaState,
} from "./WorklistGateStates";
import {
  BUCKET_ORDER,
  PatientPriorityIndex,
  type PanelProvider,
  type PatientPriorityIndexSelectedMember,
  type PriorityBucket,
} from "@astrana/ui/modules";

/**
 * `?bucket=` → the module's `initialView`, or undefined.
 *
 * Validated against `BUCKET_ORDER` (the module's own union, exported for
 * exactly this) rather than cast: the value arrives off a URL a user can
 * type, and a bogus `?bucket=urgnet` must open the default panel, not seed a
 * view nothing matches. An unrecognised value is dropped silently on purpose
 * — a landing page that error-toasts a malformed query param it could simply
 * ignore is worse than one that shows the panel.
 *
 * See `useLatchedBucket` below for why the result is latched rather than read
 * live.
 */
function bucketFromSearch(raw: string | null): PriorityBucket | undefined {
  if (!raw) return undefined;
  return BUCKET_ORDER.find((b) => b === raw);
}

/**
 * The `?bucket=` seed, LATCHED — it only ever moves forward to a real bucket,
 * never back to `undefined`.
 *
 * ⚠️ WHY THIS IS NOT JUST `bucketFromSearch(searchParams.get("bucket"))`.
 * `TabbedAppShell` is a KEEP-ALIVE shell: once the worklist tab has been
 * activated it stays mounted and is merely `hidden` (TabbedAppShell.tsx:316).
 * A mounted-but-hidden page still re-renders on every route change, and
 * `useSearchParams()` reports the CURRENT location — so the moment the
 * provider switches to Home, this page reads `bucket=null`.
 *
 * That matters because the module applies `initialView` as a React `key`
 * (PatientPriorityIndex.tsx). An unlatched read would therefore change the
 * key from "urgent" back to "all" on every tab switch away, REMOUNTING the
 * whole panel and throwing away its view, sort, search and scroll. Latching
 * pins the key, so the panel survives tab switches exactly as it does today.
 *
 * State-during-render rather than `useEffect`: this is deriving state from a
 * changed prop/param, which is the one case React sanctions setting state in
 * the render body for (it re-runs the component immediately, before painting)
 * — and an effect here would paint the reset frame first, which is the very
 * remount this exists to prevent.
 */
function useLatchedBucket(): PriorityBucket | undefined {
  const [searchParams] = useSearchParams();
  const fromUrl = bucketFromSearch(searchParams.get("bucket"));
  const [latched, setLatched] = useState(fromUrl);
  if (fromUrl && fromUrl !== latched) setLatched(fromUrl);
  return latched;
}

/**
 * Host page for @astrana/ui's native Patient Priority Index module (P6 host
 * wiring) — a provider's ranked patient worklist, rendered at the top-level
 * `/worklist` route (see App.tsx's gating: featureFlags.patientPriorityIndex
 * AND useModuleEnabled("PriorityWorklist")). Mirrors how MemberProfilePage
 * wires the native Create Auth module's config/identity props, but this
 * surface is PROVIDER-scoped (no member context) rather than member-scoped.
 *
 * ACT-1499 — provider scope uses Desktop's lazy two-column picker
 * (`providerRosterLazy`: App/* page + react-window), not an eager full roster
 * and not the old NPI | IPA identity-bar ScopeSwitchers.
 */
export default function WorklistPage() {
  return <WorklistPageReal />;
}

function WorklistPageReal() {
  const navigate = useNavigate();
  // Deep-link entry point: Home's UrgentPatientsCard count tiles link here as
  // `/worklist?bucket=urgent` | `?bucket=highRisk`.
  const initialView = useLatchedBucket();
  const { selectedProvider, accessToken, accountLabel } = useAuthContext();
  const { isDark } = useDarkMode();

  // P6c SHIP-GATE — PP bearer only. Desktop-parity lazy roster owns provider/
  // IPA scope inside PPI; do NOT call ProviderOverview/EntitledIpas here
  // (unused for this surface; Home/AdtBell still fetch when they need it).
  const accessGate = useWorklistAccessGate({ fetchEntitlements: false });

  const identityEmail = resolveIdentityEmail(accountLabel);

  // Stable ref so `getAccessToken` always reads the LATEST auth state without
  // forcing the module's `getAccessToken` prop identity (and therefore its
  // module-scoped RTK Query baseQuery closure) to be recreated on every token
  // refresh. Mirrors the "stable ref getter" pattern the Apollo authLink uses
  // (see docs/architecture-overview.md).
  const authRef = useRef({ selectedProvider, accessToken, identityEmail });
  authRef.current = { selectedProvider, accessToken, identityEmail };

  const getAccessToken = useCallback((): string => {
    const { selectedProvider: sp, accessToken: at, identityEmail: email } =
      authRef.current;
    // Real PP B2C bearer — NEVER a mint/exchange token (see
    // PatientPriorityIndexConfig.getAccessToken's contract).
    return getPpToken(sp, at, email) ?? "";
  }, []);

  const companyId = accessGate.resolvedIpaId ?? "";

  // userId / userRole: MUST be decoded off the SAME bearer `getAccessToken`
  // actually returns (the Provider Portal bearer via `getPpToken`), never the
  // primary Entra/Pathways session token directly — for an SSO/Pathways
  // provider with a stashed *linked* PP token, the two bearers carry
  // different `sub` claims, and PP-API's `IsUserValid`/`AuthEnabledProviderList`
  // compare userId against the *bearer's* sub. Decoding from `accessToken`
  // (as before) sent the Entra sub while the PP bearer went out on the wire →
  // 403. Mirrors MemberProfilePage.tsx's `identityToken`/`nativeUserId`
  // pattern; see resolveWorklistIdentity.ts for the (unit-tested) decode.
  const ppBearerToken = getPpToken(selectedProvider, accessToken, identityEmail);
  const { userId: nativeUserId, userRole: nativeUserRole } =
    resolveWorklistIdentity(ppBearerToken);
  const oid = jwtUserOid(accessToken) ?? undefined;

  const ppApiBaseUrl = `${import.meta.env.VITE_PROVIDER_PORTAL_API_BASE_URL}/api`;

  // Seed NPI/IPA from persisted picks (Desktop restore) or the access gate —
  // no eager full-roster fetch. The lazy bar pages providers on open.
  const storedPicks = useMemo(() => getStoredProviderPicks(oid), [oid]);
  const seed = useMemo(() => {
    const storedWithNpi = storedPicks.find((p) => !!p.npi?.trim());
    if (storedWithNpi?.npi) {
      return {
        pcpNpi: storedWithNpi.npi.trim(),
        ipaId: storedWithNpi.ipa || companyId,
      };
    }
    return {
      pcpNpi: (accessGate.npi ?? "").trim(),
      ipaId: companyId,
    };
  }, [storedPicks, accessGate.npi, companyId]);

  const handleProviderSelectionChange = useCallback(
    (next: PanelProvider[]) => {
      setStoredProviderPicks(oid, picksFromProviders(next));
      // Keep gate NPI/IPA in sync for AdtBell / other consumers of the access
      // gate that still read a single active (npi, ipa) pair.
      const first = next.find((p) => !!p.npi?.trim());
      if (first) {
        if (first.ipa && first.ipa !== companyId) {
          accessGate.selectIpa(first.ipa);
        }
        if (first.npi && first.npi !== accessGate.npi) {
          accessGate.selectNpi(first.npi);
        }
      }
    },
    [oid, companyId, accessGate],
  );

  const handleSelectMember = useCallback(
    (member: PatientPriorityIndexSelectedMember) => {
      const astranaId = String(member.astranaId);
      navigate(`/members/astrana:${encodeURIComponent(astranaId)}`, {
        state: buildWorklistMemberNavState(astranaId, member),
      });
    },
    [navigate],
  );

  const handleError = useCallback((e: unknown) => {
    // Log the status only, never the raw error object — an HTTP error's
    // `data` is the response BODY and can carry PHI. Same restriction and
    // helper AdtBell.tsx's `handleError` uses (adtBellLogic.ts's
    // `adtBellErrorKey`, deliberately reads ONLY `status`).
    // eslint-disable-next-line no-console
    console.error(`[worklist] Patient Priority Index error (status=${adtBellErrorKey(e)})`);
    toast.error("Something went wrong loading the priority worklist.");
  }, []);

  if (accessGate.status !== "ready") {
    return (
      <div className="h-full min-h-0 flex flex-col">
        {accessGate.status === "no-pp-session" ? (
          <PpLoginRequiredState
            identityEmail={accessGate.identityEmail}
            onLogin={accessGate.requestPpLogin}
          />
        ) : accessGate.status === "resolving-ipa" ? (
          <ResolvingIpaState />
        ) : accessGate.status === "ipa-error" ? (
          <IpaErrorState onRetry={accessGate.retryIpas} />
        ) : accessGate.status === "ipa-forbidden" ? (
          <IpaForbiddenState />
        ) : accessGate.status === "no-ipa" ? (
          <NoIpaState />
        ) : (
          <NpiRequiredState error={accessGate.npiError} onSubmit={accessGate.submitNpi} />
        )}
        {accessGate.ppLoginOpen && accessGate.identityEmail && (
          <PpLoginModal
            email={accessGate.identityEmail}
            onSuccess={accessGate.handlePpLoginSuccess}
            onCancel={accessGate.cancelPpLogin}
          />
        )}
      </div>
    );
  }

  // Empty `pcpNpi` is fine on the lazy path — the Desktop-parity bar opens
  // without a seed and RiskEvidence shows its "select a provider" placeholder
  // until Apply. Gate NPI / stored picks only seed the initial fetch.
  return (
    <div className="h-full min-h-0 flex flex-col">
      <PatientPriorityIndex
        ppApiBaseUrl={ppApiBaseUrl}
        companyId={seed.ipaId || companyId}
        getAccessToken={getAccessToken}
        pcpNpi={seed.pcpNpi}
        ipaId={seed.ipaId || companyId}
        userId={nativeUserId ?? ""}
        userRole={nativeUserRole}
        userEmail={identityEmail}
        onSelectMember={handleSelectMember}
        onError={handleError}
        theme={isDark ? "dark" : "light"}
        initialView={initialView}
        providerRosterLazy
        onProviderSelectionChange={handleProviderSelectionChange}
      />
    </div>
  );
}
